import React, { useEffect, useState } from 'react'
import { Form, Row, Col, Upload, Skeleton } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface'
import { Input, DatepickerCustom } from 'components'
import locale from 'antd/es/date-picker/locale/pt_BR'
import { FormInstance } from 'antd/lib/form'
import { IAddNewClientErros, IAddNewClientValues } from 'egi/app/Leads/leadInterfaces'
import SimulationModel from 'egi/models/SimulationModel'
import ApiPartnerDocumentsRepository, { IApiPartnerNecessaryDocuments } from 'egi/repositories/ApiPartnerDocumentsRepository'

interface IClientDocument {
  key: keyof IApiPartnerNecessaryDocuments
  title: string
}

const CLIENT_DOCUMENT_LABELS: Record<keyof IApiPartnerNecessaryDocuments, string> = {
  comprovanteEndereco: 'Comprovante de endereço',
  holerite: 'Holerite',
  ultimoComprovantePagamentoBeneficio: 'Último comprovante de pagamento de benefício',
  tresUltimosExtratos: 'Três últimos extratos',
  extratoBancario: 'Extrato bancário',
  cnpjContratoSocial: 'CNPJ e Contrato Social'
}

interface IClientForm {
  form: FormInstance<IAddNewClientValues>
  onFinish: (values: IAddNewClientValues) => void
  errors?: IAddNewClientErros
  personType: string | undefined
  simulationId?: string

  onCpfBlur: (event: React.ChangeEvent<HTMLInputElement>) => void
  onEmailBlur: (event: React.ChangeEvent<HTMLInputElement>) => void
}

function getEnabledClientDocuments (documents: IApiPartnerNecessaryDocuments): IClientDocument[] {
  return (Object.keys(CLIENT_DOCUMENT_LABELS) as Array<keyof IApiPartnerNecessaryDocuments>)
    .filter((documentKey) => documents[documentKey])
    .map((documentKey) => ({
      key: documentKey,
      title: CLIENT_DOCUMENT_LABELS[documentKey]
    }))
}

function getUploadFileList (event: UploadChangeParam<UploadFile>): UploadFile[] {
  return event?.fileList ?? []
}

function ClientForm ({ form, onFinish, personType, simulationId, errors, onCpfBlur, onEmailBlur }: IClientForm) {
  const [clientDocuments, setClientDocuments] = useState<IClientDocument[]>([])
  const [loadingClientDocuments, setLoadingClientDocuments] = useState<boolean>(false)

  useEffect(() => {
    const abortController = new AbortController()

    async function getClientDocuments () {
      if (!simulationId) {
        setClientDocuments([])
        setLoadingClientDocuments(false)
        return
      }

      setLoadingClientDocuments(true)

      try {
        const response = await ApiPartnerDocumentsRepository.necessaryDocuments(simulationId, {
          signal: abortController.signal
        })
        const necessaryDocuments = response.data.data?.documents
        const documents = necessaryDocuments ? getEnabledClientDocuments(necessaryDocuments) : []

        if (!abortController.signal.aborted) setClientDocuments(documents)
      } catch (err) {
        if (abortController.signal.aborted) return

        setClientDocuments([])
      }

      if (!abortController.signal.aborted) setLoadingClientDocuments(false)
    }

    getClientDocuments()

    return () => {
      abortController.abort()
    }
  }, [simulationId])

  const canSeeClientDocuments = Boolean(simulationId && (loadingClientDocuments || clientDocuments.length))

  return (
    <Form
      form={form}
      onFinish={onFinish}
      layout="vertical"
    >
      <Row>
        <Col lg={12} sm={24} xs={24} className="px-1">
          <Form.Item
            name="email"
            help={errors?.email && errors.email}
            validateStatus={errors?.email && 'error'}
            rules={[{ required: true, message: 'Digite o email do cliente.' }]}
            label={<label>Email</label>}
          >
            <Input
              placeholder="Digite o email do cliente"
              error={errors?.email}
              onBlur={onEmailBlur}
            />
          </Form.Item>
        </Col>

        <Col lg={12} sm={24} xs={24} className="px-1">
          <Form.Item
            name="name"
            label={<label>Nome</label>}
            help={errors?.name && errors.name}
            validateStatus={errors?.name && 'error'}
            rules={[{ required: true, message: 'Digite o nome do cliente.' }]}
          >
            <Input placeholder="Digite o nome do cliente" />
          </Form.Item>
        </Col>

        <Col lg={8} sm={24} xs={24} className="px-1">
          <Form.Item
            name="cpf"
            label={<label>CPF</label>}
            help={errors?.cpf && errors?.cpf}
            validateStatus={errors?.cpf && 'error'}
            rules={[{ required: true, message: 'Digite o cpf do cliente.' }]}
          >
            <Input
              placeholder="Digite o cpf do cliente"
              mask="cpf"
              error={errors?.cpf}
              onBlur={onCpfBlur}
            />
          </Form.Item>
        </Col>

        <Col lg={8} sm={24} xs={24} className="px-1">
          <Form.Item
            name="cellphone"
            label={<label>Celular</label>}
            help={errors?.cellphone && errors.cellphone}
            validateStatus={errors?.cellphone && 'error'}
            rules={[{ required: true, message: 'Digite o celular do cliente.' }]}
          >
            <Input
              placeholder="Digite o celular do cliente"
              mask="phone"
              error={errors?.cellphone}
            />
          </Form.Item>
        </Col>

        {SimulationModel.canSeeBirthdateField(personType) &&
          <Col lg={8} sm={24} xs={24} className="px-1">
            <Form.Item
              name="birthdate"
              label={<label>Data de nascimento</label>}
              help={errors?.birthdate && errors.birthdate}
              validateStatus={errors?.birthdate && 'error'}
              rules={[{ required: true, message: 'Escolha a data de nascimento do cliente!' }]}
            >
              <DatepickerCustom locale={locale} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
        }

        {canSeeClientDocuments && (
          <Col lg={24} sm={24} xs={24} className="px-1">
            <h2 className="simulator-documents__title">Documentos</h2>

            {loadingClientDocuments ? (
              <Skeleton active />
            ) : clientDocuments.map((document) => (
              <div className="simulator-documents__item" key={document.key}>
                <label className="simulator-result-label">{document.title}</label>

                <Form.Item
                  name={['documents', document.key]}
                  valuePropName="fileList"
                  getValueFromEvent={getUploadFileList}
                >
                  <Upload.Dragger
                    accept="image/png,image/jpeg,image/jpg"
                    beforeUpload={() => false}
                    maxCount={1}
                  >
                    <p className="simulator-documents__icon">
                      <InboxOutlined />
                    </p>
                    <p className="simulator-documents__text">Clique ou arraste a imagem para anexar</p>
                  </Upload.Dragger>
                </Form.Item>
              </div>
            ))}
          </Col>
        )}
      </Row>
    </Form>
  )
}

export default ClientForm
